type DownloadOptions = {
  readonly url: string;
  readonly file_name: string;
  readonly is_app?: boolean;
  readonly onError?: (error: Error) => void;
  readonly onSuccess?: () => void;
};

export const sanitizeFileName = (fileName: string) => {
  return fileName.replace(/\s+/g, '').replace(/\.(png|jpe?g|webp|gif)$/i, '');
};

const createDownloadLink = (url: string, file_name: string): HTMLAnchorElement => {
  const link = document.createElement('a');
  link.href = url;
  link.download = file_name;
  link.style.display = 'none';
  return link;
};

const triggerDownload = (link: HTMLAnchorElement): void => {
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const isBase64DataUrl = (url: string): boolean => {
  return url.startsWith('data:');
};

export const base64ToBlob = async (dataUrl: string): Promise<string> => {
  const base64Data = dataUrl.split(',')[1];

  if (!base64Data) {
    throw new Error('Invalid base64 data URL');
  }

  const byteCharacters = window.atob(base64Data);
  const byteArrays = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays[i] = byteCharacters.charCodeAt(i);
  }

  const mimeType = dataUrl.match(/^data:([^;]+);base64,/)?.[1] || 'application/octet-stream';
  const blob = new Blob([byteArrays], { type: mimeType });

  return window.URL.createObjectURL(blob);
};

const fetchAndDownload = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.blob();
};

const handleWebDownload = async (url: string, file_name: string): Promise<void> => {
  let blob: Blob;
  let objectUrl: string | null = null;

  if (isBase64DataUrl(url)) {
    objectUrl = await base64ToBlob(url);
    blob = await fetchAndDownload(objectUrl);
  } else {
    blob = await fetchAndDownload(url);
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = createDownloadLink(blobUrl, file_name);
  triggerDownload(link);

  // Cleanup object URLs
  window.URL.revokeObjectURL(blobUrl);
  if (objectUrl) {
    window.URL.revokeObjectURL(objectUrl);
  }
};

const handleAppDownload = async (url: string, file_name: string): Promise<void> => {
  // const downloadUrl = isBase64DataUrl(url) ? await base64ToBlob(url) : url;
  // const link = createDownloadLink(downloadUrl, file_name);
  // triggerDownload(link);
  //
  // if (downloadUrl !== url) {
  //   window.URL.revokeObjectURL(downloadUrl);
  // }

  // TODO @yjj 앱 이미지 엘범으로 다운로드 만들어지기 전까지 임시로 디지털/전자책 zip 다운로드 로직 이용
  return window.ReactNativeWebView.postMessage(
    JSON.stringify({
      downloadDigitalProduct: [
        {
          signed_url: url,
          file_name,
        },
      ],
      // TODO @blan22 함수로 빼두고, 파일 형식 필터링 정규식 단단하게 가져가기
      zipFileName: `marppleShop_${Date.now()}.zip`,
    }),
  );
};

export const downloadFileFromUrl = async ({
  url,
  file_name,
  is_app = false,
  onError,
  onSuccess,
}: DownloadOptions): Promise<void> => {
  try {
    if (is_app) {
      await handleAppDownload(url, file_name);
    } else {
      await handleWebDownload(url, file_name);
    }
    onSuccess?.();
  } catch (error) {
    if (error instanceof Error) {
      onError?.(error);
    }
  }
};

// URL에서 특정 쿼리 파라미터 값을 가져오는 함수
export const getQueryParam = (paramName: string): string | null => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.get(paramName);
};
